import React, { useState } from 'react';
import { X, Users, MessageSquare, Sparkles } from 'lucide-react';
import AgentPanel from './AgentPanel';
import ChatPanel from './ChatPanel';
import { AGENT_PERSONAS } from '../constants';
import type {
  BrandTeamAgentRun,
  BrandTeamAgentLog,
  BrandTeamAgentArtifact,
  BrandTeamChatMessage,
} from '../../../../services/brandTeamService';

interface AgentDrawerProps {
  open: boolean;
  onClose: () => void;
  agentRuns: BrandTeamAgentRun[];
  agentArtifacts: BrandTeamAgentArtifact[];
  agentLogs: BrandTeamAgentLog[];
  chatMessages: BrandTeamChatMessage[];
  chatInput: string;
  onChatInputChange: (value: string) => void;
  onChatSend: () => void;
  chatSending: boolean;
  chatDisabled: boolean;
}

const AgentDrawer: React.FC<AgentDrawerProps> = ({
  open,
  onClose,
  agentRuns,
  agentArtifacts,
  agentLogs,
  chatMessages,
  chatInput,
  onChatInputChange,
  onChatSend,
  chatSending,
  chatDisabled,
}) => {
  const [activeTab, setActiveTab] = useState<'agents' | 'chat'>('agents');

  const doneCount = AGENT_PERSONAS.filter((p) =>
    agentRuns.find((r) => r.agentKey === p.agentKey && r.status === 'completed')
  ).length;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-[400px] flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-primary-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand-purple" />
            <h3 className="text-sm font-bold text-text-dark">Your AI Team</h3>
            <span className="rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-600">
              {doneCount}/{AGENT_PERSONAS.length}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-primary-100">
          <button
            type="button"
            onClick={() => setActiveTab('agents')}
            className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors ${
              activeTab === 'agents'
                ? 'border-b-2 border-brand-purple text-brand-purple'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <Users className="h-3.5 w-3.5" />
            Agents
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('chat')}
            className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors ${
              activeTab === 'chat'
                ? 'border-b-2 border-brand-purple text-brand-purple'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Chat
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'agents' ? (
            <AgentPanel
              agentRuns={agentRuns}
              agentArtifacts={agentArtifacts}
              agentLogs={agentLogs}
              sessions={[]}
              activeSessionId={null}
              onSelectSession={() => {}}
            />
          ) : (
            <ChatPanel
              messages={chatMessages}
              chatInput={chatInput}
              onInputChange={onChatInputChange}
              onSend={onChatSend}
              sending={chatSending}
              disabled={chatDisabled}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AgentDrawer;
